import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { 
  Briefcase, 
  Loader2,
  User,
  Ticket,
  ChevronDown,
  BarChart3,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DEMAND_TYPE_CONFIG, SESSION_STATUS_CONFIG } from '@/hooks/useSupportInbox';
import { EffortIndicator } from './EffortIndicator';
import { SlaTimer } from './SlaTimer';
import { InteractionsTimeline } from './InteractionsTimeline';
import { SessionActions } from '@/components/customer-service';
import { ContactTasksSection } from './ContactTasksSection';
import type { DemandType, SessionStatus, SessionWithSla } from '@/types/support.types';

const ROLE_LABELS: Record<string, string> = {
  applicant: 'Applicant',
  dependent: 'Dependente',
  beneficiary: 'Beneficiário',
  petitioner: 'Peticionário',
  payer: 'Pagador',
  point_of_contact: 'Ponto de Contato',
};

interface ClientSidebarProps {
  contactId: string | null;
  sessionId: string | null;
  onSelectSession?: (session: any) => void;
  onScrollToSession?: (sessionId: string) => void;
  session?: SessionWithSla | null;
  existingDemandTypes?: DemandType[];
  onSessionUpdated?: () => void;
  onNewSessionCreated?: (sessionId: string) => void;
  canActAsManager?: boolean;
}

export function ClientSidebar({ contactId, sessionId, onSelectSession, onScrollToSession, session, existingDemandTypes = [], onSessionUpdated, onNewSessionCreated, canActAsManager }: ClientSidebarProps) {
  const navigate = useNavigate();

  const { data: currentAppUser } = useCurrentAppUser();
  const isAdmin = currentAppUser?.system_role?.code === 'admin' || currentAppUser?.system_role?.code === 'it';
  const queryClient = useQueryClient();
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDeleteSession = async (sid: string) => {
    setDeletingSessionId(sid);
    const { error } = await supabase.from('support_sessions').delete().eq('id', sid);
    setDeletingSessionId(null);
    setConfirmDeleteId(null);
    if (error) {
      toast.error('Erro ao excluir chamado');
      return;
    }
    toast.success('Chamado excluído');
    queryClient.invalidateQueries({ queryKey: ['contact-sessions', contactId] });
    queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full', contactId] });
    queryClient.invalidateQueries({ queryKey: ['support-sessions'] });
    onSessionUpdated?.();
  };

  // Fetch related CASES (Processos)
  const { data: cases, isLoading: loadingCases } = useQuery({
    queryKey: ['support-contact-cases', contactId],
    queryFn: async () => {
      if (!contactId) return { cases: [], roleMap: {} as Record<string, string> };
      const { data: caseContacts, error: ccError } = await supabase
        .from('case_contacts')
        .select('case_id, role')
        .eq('contact_id', contactId);
      if (ccError) throw ccError;
      if (!caseContacts || caseContacts.length === 0) return { cases: [], roleMap: {} };
      const roleMap: Record<string, string> = Object.fromEntries(
        caseContacts.map(cc => [cc.case_id, cc.role])
      );
      const caseIds = [...new Set(caseContacts.map(cc => cc.case_id))];
      const { data: casesData, error: casesError } = await supabase
        .from('cases')
        .select('id, status, visa_type, created_at, item:items(catalog:catalog(name))')
        .in('id', caseIds)
        .order('created_at', { ascending: false })
        .limit(10);
      if (casesError) throw casesError;
      return { cases: casesData || [], roleMap };
    },
    enabled: !!contactId,
  });

  // Fetch sessions with SLA data for this contact
  const { data: contactSessions, isLoading: loadingSessions } = useQuery({
    queryKey: ['support-contact-sessions-full', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      const { data, error } = await supabase
        .from('v_sessions_with_sla')
        .select('*')
        .eq('contact_id', contactId)
        .neq('status', 'pre_ticket')
        .neq('status', 'auto_archived')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
    enabled: !!contactId,
  });

  const handleViewCase = (caseId: string) => {
    navigate(createPageUrl('CaseDetail') + `?id=${caseId}`);
  };

  if (!contactId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground bg-card border-l p-6">
        <User className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm text-center">Selecione um chamado para ver os detalhes do cliente</p>
      </div>
    );
  }

  // Border color per status
  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'waiting_internal': return 'border-l-amber-500';
      case 'waiting_client': return 'border-l-emerald-500';
      case 'new': case 'in_progress': return 'border-l-blue-500';
      case 'pre_ticket': return 'border-l-indigo-500';
      default: return 'border-l-muted';
    }
  };

  return (
    <div className="h-full flex flex-col bg-card border-l">
      {/* Action Bar */}
      {(canActAsManager || session) && (
        <div className="p-3 border-b flex items-center gap-2 flex-wrap">
          {canActAsManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/customer-service/dashboard')}
              className="h-8"
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
          )}
          {session && (
            <SessionActions
              session={session}
              existingDemandTypes={existingDemandTypes}
              onSessionUpdated={onSessionUpdated}
              onNewSessionCreated={onNewSessionCreated}
            />
          )}
        </div>
      )}

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        {/* (Effort Indicator movido para o final) */}


        {/* Cases Section (Processos) */}
        <Collapsible defaultOpen={false}>
        <div className="p-4">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center gap-2 mb-3 group">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Processos</h3>
              {cases?.cases && (
                <span className="text-xs text-muted-foreground">({cases.cases.length})</span>
              )}
              <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto transition-transform [[data-state=open]_&]:rotate-180" />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
          {loadingCases ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : cases?.cases && cases.cases.length > 0 ? (
            <div className="space-y-2">
              {cases.cases.map((caseItem) => {
                const role = cases.roleMap[caseItem.id];
                const roleLabel = role ? ROLE_LABELS[role] : null;
                return (
                  <div
                    key={caseItem.id}
                    onClick={() => handleViewCase(caseItem.id)}
                    className="p-2.5 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">
                        {caseItem.visa_type || caseItem.item?.catalog?.name || 'Processo'}
                      </span>
                      {roleLabel && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800 flex-shrink-0">
                          {roleLabel}
                        </Badge>
                      )}
                    </div>
                    {caseItem.status && (
                      <span className="text-xs text-muted-foreground block">{caseItem.status}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum processo vinculado
            </p>
          )}
          </CollapsibleContent>
        </div>
        </Collapsible>

        <Separator />

        {/* Tickets/Sessions Section (Chamados) */}
        <Collapsible defaultOpen={false}>
        <div className="p-4">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center gap-2 mb-3 group">
              <Ticket className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Chamados</h3>
              {contactSessions && (
                <span className="text-xs text-muted-foreground">
                  ({contactSessions.filter((s: any) => s.status !== 'pre_ticket' && s.status !== 'auto_archived').length})
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto transition-transform [[data-state=open]_&]:rotate-180" />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
          {loadingSessions ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : contactSessions && contactSessions.filter((s: any) => s.status !== 'pre_ticket' && s.status !== 'auto_archived').length > 0 ? (
            <div className="space-y-2">
              {contactSessions.filter((s: any) => s.status !== 'pre_ticket' && s.status !== 'auto_archived').map((s: any) => {
                const isCurrent = s.id === sessionId;
                const demandLabel = s.demand_type ? DEMAND_TYPE_CONFIG[s.demand_type as DemandType]?.label : null;
                const statusLabel = SESSION_STATUS_CONFIG[s.status as SessionStatus]?.label || s.status;
                const followUps = s.follow_up_count || 0;

                return (
                  <Collapsible key={s.id} defaultOpen={isCurrent}>
                    <div
                      className={cn(
                        'rounded-lg border-l-[3px] transition-colors',
                        getStatusBorderColor(s.status),
                        isCurrent ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'
                      )}
                    >
                      {/* Header: CollapsibleTrigger + botão lixeira como irmãos */}
                      <div className="flex items-center gap-1 px-2.5 pt-2.5">
                        <CollapsibleTrigger asChild>
                          <div
                            className="flex-1 min-w-0 cursor-pointer hover:bg-muted/80 rounded-lg flex items-center gap-1.5 overflow-hidden py-0.5 px-0.5"
                            onClick={() => {
                              if (!isCurrent) {
                                onSelectSession?.(s);
                              }
                              onScrollToSession?.(s.id);
                            }}
                          >
                            <span className="text-sm font-medium truncate flex-1 min-w-0">
                              {demandLabel || 'Chamado'}
                            </span>
                            <Badge variant={s.status === 'resolved' ? 'secondary' : 'outline'} className="text-[10px] h-4 px-1 flex-shrink-0 max-w-[80px] truncate">
                              {statusLabel}
                            </Badge>
                            <SlaTimer deadline={s.sla_deadline} violated={s.sla_violated} size="sm" />
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          </div>
                        </CollapsibleTrigger>
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(s.id);
                            }}
                            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
                            title="Excluir chamado"
                          >
                            {deletingSessionId === s.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />
                            }
                          </button>
                        )}
                      </div>

                      {/* Metrics row */}
                      <div className="flex items-center gap-2 px-2.5 pb-1 mt-1">
                        {followUps > 0 && (
                          <Badge variant="destructive" className="text-[10px] h-4 px-1">
                            {followUps} cobranças
                          </Badge>
                        )}
                        {s.escalated_to_dept && (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400">Escalado</span>
                        )}
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {formatDistanceToNow(new Date(s.created_at), { addSuffix: true, locale: ptBR })}
                        </span>
                      </div>

                      {isCurrent && (
                        <p className="text-xs text-primary px-2.5 pb-1 font-medium">Chamado atual</p>
                      )}

                      {s.status === 'resolved' && (s.resolution_type || s.resolution_notes) && (
                        <div className="mx-2.5 mb-1.5 p-1.5 rounded bg-muted/70 text-xs space-y-0.5">
                          {s.resolution_type && (
                            <span className="font-medium text-muted-foreground">
                              {s.resolution_type === 'resolved' ? 'Resolvido' : s.resolution_type === 'not_applicable' ? 'Não aplicável' : s.resolution_type === 'redirected' ? 'Redirecionado' : s.resolution_type}
                            </span>
                          )}
                          {s.resolution_notes && (
                            <p className="text-muted-foreground italic line-clamp-3">{s.resolution_notes}</p>
                          )}
                        </div>
                      )}

                      <CollapsibleContent>
                        <div className="px-2.5 pb-2.5 border-t border-border/50">
                          <InteractionsTimeline sessionId={s.id} />
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum chamado encontrado
            </p>
          )}
          </CollapsibleContent>
        </div>
        </Collapsible>

        <Separator />

        {/* Tasks & Escalations (consolidado do contato) */}
        <ContactTasksSection
          contactId={contactId}
          sessionId={sessionId}
          onSelectSession={onSelectSession}
        />

        <Separator />

        {/* Effort Indicator (movido para o final) */}
        <div className="p-4">
          <EffortIndicator contactId={contactId} />
        </div>
      </ScrollArea>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Chamado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. O chamado e todas as suas interações serão permanentemente excluídos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteId && handleDeleteSession(confirmDeleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingSessionId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
